<script setup lang="ts">
import { LoaderCircle, MessageSquareText, Star } from "lucide-vue-next";
import type {
  PublicWorkerReview,
  PublicWorkerReviewsPageResponse,
  PublicWorkerReviewsResponse,
  PublicWorkerServiceOption,
  PublicWorkerServiceOptionsResponse,
} from "~/types/public-worker";
import { toTitleCase } from "~/utils/search-catalog";

const props = defineProps<{
  workerId: number;
  workerName: string;
  initialData: PublicWorkerReviewsResponse;
}>();

const route = useRoute();
const localeRoute = useLocaleRoute();
const { t } = useI18n();
const { user, loggedIn } = useAuth();
const axios = useAxios();
const toast = useToast();

const data = ref<PublicWorkerReviewsResponse>(props.initialData);
const ownReview = ref<PublicWorkerReview | null>(null);
const reviewServices = ref<PublicWorkerServiceOption[]>([]);
const reviewServicesLoaded = ref(false);
const selectedServiceId = ref("");

const loading = ref(false);
const loadingMore = ref(false);
const ownReviewLoading = ref(false);
const reviewServicesLoading = ref(false);
const deletingReview = ref(false);

const loadError = ref("");
const reviewModalOpen = ref(false);
const reviewToDelete = ref<PublicWorkerReview | null>(null);

function serviceLabel(value: string) {
  const key = `services.${value}`;
  const translated = t(key);

  return translated === key ? toTitleCase(value) : translated;
}

const serviceOptions = computed(() => [
  {
    value: "",
    label: t("worker_profile.all_reviews"),
  },
  ...data.value.reviewedServices.map(service => ({
    value: String(service.id),
    label: serviceLabel(service.name),
  })),
]);

const displayedOwnReview = computed(() => {
  if (!ownReview.value) {
    return null;
  }

  if (!selectedServiceId.value) {
    return ownReview.value;
  }

  return ownReview.value.workerServiceId === Number(selectedServiceId.value)
    ? ownReview.value
    : null;
});

const publicReviews = computed(() => data.value.items);
const isOwnProfile = computed(() => user.value?.workerId === props.workerId);

const hasDisplayedReviews = computed(
  () => Boolean(displayedOwnReview.value) || publicReviews.value.length > 0,
);

function getApiErrorMessage(
  error: any,
  fallbackTranslationKey: string,
): string {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.statusMessage ||
    error?.response?.data?.data?.message ||
    t(fallbackTranslationKey)
  );
}

async function fetchReviews(
  page = 1,
  options: { append?: boolean; includeMetadata?: boolean } = {},
) {
  const { append = false, includeMetadata = false } = options;

  try {
    if (append) {
      loadingMore.value = true;
    } else {
      loading.value = true;
    }

    loadError.value = "";

    const response = await axios.get<PublicWorkerReviewsPageResponse>(
      `/workers/${props.workerId}/reviews`,
      {
        params: {
          page,
          pageSize: data.value.pagination.pageSize || 6,
          serviceId: selectedServiceId.value || undefined,
          includeMetadata,
        },
      },
    );

    const existingIds = append
      ? new Set(data.value.items.map(review => review.id))
      : null;
    const items = append
      ? [
          ...data.value.items,
          ...response.data.items.filter(review => !existingIds?.has(review.id)),
        ]
      : response.data.items;

    data.value = {
      items,
      pagination: response.data.pagination,
      summary: response.data.summary ?? data.value.summary,
      reviewedServices:
        response.data.reviewedServices ?? data.value.reviewedServices,
    };
  } catch (error) {
    console.error("Fetch reviews failed:", error);

    loadError.value = getApiErrorMessage(
      error,
      "worker_profile.reviews_load_error",
    );
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
}

async function fetchOwnReview() {
  if (!loggedIn.value) {
    ownReview.value = null;
    return;
  }

  try {
    ownReviewLoading.value = true;

    const response = await axios.get<{
      item: PublicWorkerReview | null;
    }>(`/internal/workers/${props.workerId}/reviews/mine`);

    ownReview.value = response.data.item;
  } catch (error: any) {
    if (error?.response?.status !== 401 && error?.response?.status !== 404) {
      console.error("Fetch own review failed:", error);
    }

    ownReview.value = null;
  } finally {
    ownReviewLoading.value = false;
  }
}

async function selectService(value: string) {
  selectedServiceId.value = value;
  await fetchReviews(1);
}

async function loadMoreReviews() {
  if (
    loadingMore.value ||
    data.value.pagination.page >= data.value.pagination.totalPages
  ) {
    return;
  }

  await fetchReviews(data.value.pagination.page + 1, { append: true });
}

async function refreshReviews() {
  selectedServiceId.value = "";

  await Promise.all([
    fetchReviews(1, { includeMetadata: true }),
    fetchOwnReview(),
  ]);
}

async function openReviewModal() {
  if (!loggedIn.value) {
    navigateTo(
      localeRoute({
        path: "/login",
        query: {
          redirect: `${route.fullPath.split("#")[0]}#reviews`,
        },
      }),
    );

    return;
  }

  if (isOwnProfile.value || ownReview.value) {
    return;
  }

  if (!reviewServicesLoaded.value) {
    try {
      reviewServicesLoading.value = true;

      const response = await axios.get<PublicWorkerServiceOptionsResponse>(
        `/workers/${props.workerId}/services/options`,
      );

      reviewServices.value = response.data.items;
      reviewServicesLoaded.value = true;
    } catch (error) {
      console.error("Fetch review service options failed:", error);

      toast.error({
        title: t("worker_profile.services_load_error"),
      });

      return;
    } finally {
      reviewServicesLoading.value = false;
    }
  }

  reviewModalOpen.value = true;
}

async function deleteOwnReview() {
  if (!reviewToDelete.value) {
    return;
  }

  try {
    deletingReview.value = true;

    await axios.delete(`/internal/reviews/${reviewToDelete.value.id}`);

    toast.success({
      title: t("worker_profile.review_deleted_title"),
      description: t("worker_profile.review_deleted_description"),
    });

    reviewToDelete.value = null;

    await refreshReviews();
  } catch (error) {
    console.error("Delete review failed:", error);

    toast.error({
      title: t("worker_profile.review_delete_error_title"),
      description: getApiErrorMessage(
        error,
        "worker_profile.review_delete_error_description",
      ),
    });
  } finally {
    deletingReview.value = false;
  }
}

onMounted(() => {
  void fetchOwnReview();
});

watch([loggedIn, () => user.value?.id], () => {
  if (import.meta.client) {
    void fetchOwnReview();
  }
});
</script>

<template>
  <section id="reviews" class="scroll-mt-36 py-10 sm:pb-0 sm:pt-12">
    <WorkerPublicSectionHeading
      :title="$t('worker_profile.reviews')"
      :description="
        $t('worker_profile.based_on_reviews', {
          count: data.summary.count,
        })
      "
    />

    <div
      class="mt-5 grid gap-4 sm:grid-cols-[minmax(0,1.45fr)_minmax(240px,0.85fr)]"
    >
      <div class="rounded-xl border border-app-border bg-white p-5 sm:p-6">
        <div class="grid grid-cols-[124px_minmax(0,1fr)] gap-5">
          <div>
            <div class="text-4xl font-bold leading-none text-app-text">
              {{ data.summary.average.toFixed(1) }}
            </div>

            <div class="mt-2 flex gap-0.5">
              <Star
                v-for="star in 5"
                :key="star"
                class="size-4"
                :class="
                  star <= Math.round(data.summary.average)
                    ? 'fill-amber-400 text-amber-400'
                    : 'text-app-border'
                "
              />
            </div>

            <p class="mt-3 text-sm leading-5 text-app-muted">
              {{
                $t("worker_profile.based_on_reviews", {
                  count: data.summary.count,
                })
              }}
            </p>
          </div>

          <div class="space-y-2">
            <div
              v-for="star in [5, 4, 3, 2, 1]"
              :key="star"
              class="grid grid-cols-[30px_minmax(0,1fr)_28px] items-center gap-2.5 text-sm text-app-muted"
            >
              <div class="flex items-center gap-1 font-semibold text-app-text">
                <span>{{ star }}</span>
                <Star class="size-3.5 fill-amber-400 text-amber-400" />
              </div>

              <div class="h-2 overflow-hidden rounded-full bg-gray-100">
                <div
                  class="h-full rounded-full bg-amber-400 transition-[width] duration-300"
                  :style="{
                    width: data.summary.count
                      ? `${
                          (data.summary.distribution[
                            star as 1 | 2 | 3 | 4 | 5
                          ] /
                            data.summary.count) *
                          100
                        }%`
                      : '0%',
                  }"
                />
              </div>

              <span class="text-right">
                {{ data.summary.distribution[star as 1 | 2 | 3 | 4 | 5] }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="rounded-xl border border-app-border bg-white p-5 sm:p-6">
        <h3 class="text-base font-semibold text-app-text">
          {{ $t("worker_profile.share_experience") }}
        </h3>

        <p class="mt-2 text-sm leading-6 text-app-muted">
          {{ $t("worker_profile.share_experience_description") }}
        </p>

        <AppTooltip
          v-if="ownReview"
          class="mt-4 w-full cursor-not-allowed"
          :text="$t('worker_profile.review_already_exists_tooltip')"
        >
          <button
            type="button"
            disabled
            class="pointer-events-none flex h-11 w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-primary-50 px-4 text-sm font-semibold text-primary/70"
          >
            <MessageSquareText class="size-4" />

            {{
              ownReview.moderationStatus === "pending"
                ? $t("worker_profile.review_pending_moderation")
                : $t("worker_profile.your_review")
            }}
          </button>
        </AppTooltip>

        <AppTooltip
          v-else-if="isOwnProfile"
          class="mt-4 w-full cursor-not-allowed"
          :text="$t('worker_profile.cannot_review_self_description')"
        >
          <button
            type="button"
            disabled
            class="pointer-events-none flex h-11 w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-primary-50 px-4 text-sm font-semibold text-primary/70"
          >
            <MessageSquareText class="size-4" />
            {{ $t("worker_profile.leave_review") }}
          </button>
        </AppTooltip>

        <button
          v-else
          type="button"
          class="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="ownReviewLoading || reviewServicesLoading"
          @click="openReviewModal"
        >
          <LoaderCircle
            v-if="reviewServicesLoading"
            class="size-4 animate-spin"
          />
          <MessageSquareText v-else class="size-4" />
          {{ $t("worker_profile.leave_review") }}
        </button>
      </div>
    </div>

    <div v-if="ownReviewLoading" class="mt-5">
      <div
        class="rounded-xl border border-primary/25 bg-primary-50/40 p-5 sm:p-6"
      >
        <div class="flex items-center gap-3">
          <AppSkeleton class="size-11 rounded-full" />

          <div>
            <AppSkeleton class="h-5 w-32 rounded" />
            <AppSkeleton class="mt-2 h-4 w-24 rounded" />
          </div>
        </div>

        <AppSkeleton class="mt-5 h-4 w-full rounded" />
        <AppSkeleton class="mt-2 h-4 w-4/5 rounded" />
      </div>
    </div>

    <WorkerPublicReviewCard
      v-else-if="displayedOwnReview"
      class="mt-5"
      :review="displayedOwnReview"
      :actions-disabled="deletingReview"
      @delete="reviewToDelete = $event"
    />

    <div
      v-if="data.summary.count > 0 && data.reviewedServices.length > 0"
      class="mt-4 flex items-center justify-between gap-3"
    >
      <div class="w-full sm:max-w-[300px]">
        <AppSelect
          :model-value="selectedServiceId"
          :options="serviceOptions"
          width="full"
          :sheet-title="$t('worker_profile.review_filter')"
          trigger-class="flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-app-border bg-white px-3.5 text-left text-sm font-semibold text-app-text transition hover:border-primary/40"
          @update:model-value="selectService"
        />
      </div>
    </div>

    <p
      v-if="loadError"
      class="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-app-danger"
    >
      {{ loadError }}
    </p>

    <div class="relative mt-3 min-h-24">
      <div v-if="loading" class="grid gap-3 sm:grid-cols-2">
        <div
          v-for="index in data.pagination.pageSize"
          :key="index"
          class="rounded-xl border border-app-border bg-white p-5"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="flex min-w-0 items-center gap-3">
              <AppSkeleton class="size-10 rounded-full" />

              <div class="min-w-0">
                <AppSkeleton class="h-5 w-32 rounded" />
                <AppSkeleton class="mt-2 h-4 w-24 rounded" />
              </div>
            </div>

            <AppSkeleton class="h-4 w-16 rounded" />
          </div>

          <AppSkeleton class="mt-4 h-6 w-32 rounded" />
          <AppSkeleton class="mt-4 h-4 w-full rounded" />
          <AppSkeleton class="mt-2 h-4 w-4/5 rounded" />
          <AppSkeleton class="mt-2 h-4 w-2/3 rounded" />
        </div>
      </div>

      <div v-else-if="publicReviews.length" class="grid gap-3 sm:grid-cols-2">
        <WorkerPublicReviewCard
          v-for="review in publicReviews"
          :key="review.id"
          :review="review"
        />
      </div>

      <div
        v-else-if="!hasDisplayedReviews"
        class="rounded-xl border border-dashed border-app-border bg-gray-50 px-6 py-12 text-center"
      >
        <MessageSquareText class="mx-auto size-8 text-app-placeholder" />

        <p class="mt-3 text-base font-semibold text-app-text">
          {{ $t("worker_profile.no_reviews") }}
        </p>

        <p class="mx-auto mt-1.5 max-w-xl text-sm leading-6 text-app-muted">
          {{ $t("worker_profile.no_reviews_description") }}
        </p>
      </div>
    </div>

    <div
      v-if="
        data.items.length && data.pagination.page < data.pagination.totalPages
      "
      class="mt-5 flex justify-center"
    >
      <button
        type="button"
        class="inline-flex h-11 min-w-[180px] items-center justify-center gap-2 rounded-lg border border-app-border bg-white px-5 text-sm font-semibold text-app-text transition hover:border-primary/35 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="loadingMore"
        @click="loadMoreReviews"
      >
        <LoaderCircle v-if="loadingMore" class="size-4 animate-spin" />

        {{ $t("worker_profile.show_more") }}
      </button>
    </div>

    <WorkerPublicReviewModal
      v-model="reviewModalOpen"
      :worker-id="workerId"
      :worker-name="workerName"
      :services="reviewServices"
      @submitted="refreshReviews"
    />

    <AppConfirmDialog
      :model-value="Boolean(reviewToDelete)"
      :title="$t('worker_profile.delete_review_title')"
      :description="$t('worker_profile.delete_review_description')"
      :confirm-text="$t('worker_profile.delete_review_confirm')"
      :cancel-text="$t('worker_profile.cancel')"
      variant="danger"
      :loading="deletingReview"
      @update:model-value="
        value => {
          if (!value) {
            reviewToDelete = null;
          }
        }
      "
      @confirm="deleteOwnReview"
    />
  </section>
</template>
